import { useCallback, useEffect, useMemo, useRef } from 'react';
import toast, { Renderable, Toast, ToastPosition } from 'react-hot-toast';
import { uuid4 } from '@sentry/utils';

type UseToastOptions = {
  visible: boolean;
  duration?: number;
  position?: ToastPosition;
  removeDelay?: number;
  render: (toastState: Toast) => Renderable;
};

const useToast = ({
  visible,
  duration = Infinity,
  position = 'top-center',
  removeDelay = 200,
  render,
}: UseToastOptions) => {
  const toastIdRef = useRef<string | null>(null);

  const id = useMemo(() => uuid4(), []);

  const clearToast = useCallback(() => {
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!visible) {
      clearToast();
      return;
    }

    const toastId = toast.custom(render, {
      id,
      duration,
      position,
      removeDelay,
    });

    toastIdRef.current = toastId;

    return () => clearToast();
  }, [duration, position, removeDelay, visible, id, render, clearToast]);
};

export default useToast;
